// Thin axios wrapper for /api/diagia/* endpoints.
//
// Reads the JWT from localStorage (same key used everywhere else in the app),
// decodes it client-side to grab the user_id, and forwards it as X-User-ID —
// same trust model used by /api/projects/*. Backend will 401 if it's missing.
//
// Re-uses BACKEND_MAIN_URL so prod / local switching is automatic.

import axios from 'axios';
import { useMemo } from 'react';
import { BACKEND_MAIN_URL } from '../../config';

function decodeTokenPayload(token) {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function getDiagiaAuthHeaders() {
  const token = localStorage.getItem('token');
  const payload = decodeTokenPayload(token);
  const userId = payload?.userId || payload?.user_id || payload?.sub || null;
  const email  = payload?.email || null;
  const headers = {};
  if (userId) headers['X-User-ID'] = userId;
  const jwt = localStorage.getItem('token');
  if (jwt) headers.Authorization = `Bearer ${jwt}`;
  // Forwarding the email from the JWT lets the DiagIA admin check work
  // even when our backend can't talk to the prod auth-api's /internal route.
  if (email)  headers['X-User-Email'] = email;
  if (token)  headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export default function useDiagiaApi() {
  return useMemo(() => {
    const baseURL = `${BACKEND_MAIN_URL}/api/diagia`;
    const client = axios.create({ baseURL });

    client.interceptors.request.use((config) => {
      const headers = getDiagiaAuthHeaders();
      config.headers = { ...(config.headers || {}), ...headers };
      return config;
    });

    return {
      // Identity probe — never throws on guest, returns {authenticated: false}.
      async whoami() {
        const { data } = await client.get('/me');
        return data;
      },
      async saveDiagnostic({ markdown, transcript, language, campaignId, inviteToken }) {
        const { data } = await client.post('/diagnostic/save', {
          markdown,
          messages: transcript,
          language,
          campaign_id: campaignId || null,
          invite_token: inviteToken || null,
        });
        return data;
      },
      async listMyDiagnostics({ limit = 50 } = {}) {
        const { data } = await client.get('/me/diagnostics', { params: { limit } });
        return data.items || [];
      },
      async getMyDiagnostic(id) {
        const { data } = await client.get(`/me/diagnostics/${id}`);
        return data;
      },
      async getDashboard() {
        const { data } = await client.get('/dashboard');
        return data;
      },

      // ----- Campaigns (Phase 2) -----
      async listCampaigns() {
        const { data } = await client.get('/campaigns');
        return data.items || [];
      },
      async getCampaign(id) {
        const { data } = await client.get(`/campaigns/${id}`);
        return data;
      },
      async createCampaign({ name, scope, description, inviteEmails, status }) {
        const { data } = await client.post('/campaigns', {
          name,
          scope: scope || null,
          description: description || null,
          invite_emails: inviteEmails || [],
          status: status || 'active',
        });
        return data;
      },
      async updateCampaign(id, patch) {
        const { data } = await client.patch(`/campaigns/${id}`, patch);
        return data;
      },
      async deleteCampaign(id) {
        await client.delete(`/campaigns/${id}`);
      },
      async addCampaignParticipants(id, emails) {
        const { data } = await client.post(`/campaigns/${id}/participants`, { emails });
        return data;
      },

      // ----- Participants (Phase 3) -----
      async listParticipants() {
        const { data } = await client.get('/participants');
        return data.items || [];
      },

      // ----- Results (Phase 4) -----
      async getResults() {
        const { data } = await client.get('/results');
        return data;
      },

      // ----- In-app invitations (accept / decline) -----
      async listMyInvitations() {
        const { data } = await client.get('/me/invitations');
        return data;
      },
      async acceptInvitation(token) {
        const { data } = await client.post(`/me/invitations/${token}/accept`);
        return data;
      },
      async declineInvitation(token) {
        const { data } = await client.post(`/me/invitations/${token}/decline`);
        return data;
      },

      // ----- Public invite flow (Phase 3) — no auth header forwarded -----
      // Uses raw axios because the interceptor adds headers we don't want
      // leaking to the public endpoint.
      async lookupInvite(token) {
        const url = `${BACKEND_MAIN_URL}/api/diagia/invite/${token}`;
        const { data } = await axios.get(url);
        return data;
      },
      async startInvite(token) {
        const url = `${BACKEND_MAIN_URL}/api/diagia/invite/${token}/start`;
        const { data } = await axios.post(url);
        return data;
      },
      async saveInviteDiagnostic(token, { markdown, transcript, language }) {
        const url = `${BACKEND_MAIN_URL}/api/diagia/invite/${token}/save`;
        const { data } = await axios.post(url, {
          markdown,
          messages: transcript,
          language,
        });
        return data;
      },
    };
  }, []);
}
